import {
  BasicOverlay,
  BasicOverlayData,
} from "$/game/prefabs/scenes/overlays/BasicOverlay.ts";
import { RoundedTextButton } from "$/game/prefabs/ui/RoundedTextButton.ts";
import { sendPreparedMessage } from "$/common/telegram-utils.ts";

export type GameOverOverlayData = BasicOverlayData & {
  isWin: boolean;
  score: number;
  record?: number; // Add optional record field
  coinsGained?: number; // Add coins gained field
  onRestart: () => void;
  onBack: () => void;
};

export class GameOverOverlay extends BasicOverlay {
  constructor() {
    super({ key: "GameOverOverlay" });
  }

  override create(data: GameOverOverlayData) {
    // Call super.create with showCloseButton set to false
    super.create({ ...data, showCloseButton: false });

    try {
      this.telegram.showAd("gameOver");
    } catch {
      console.log("error on showing game over ad");
    }

    const { width, height } = this.cameras.main;

    // Game over text
    const gameOverText = this.add.text(
      width / 2,
      300,
      "GAME OVER!",
      {
        fontSize: "48px",
        color: "#E97946",
        fontFamily: "Phantom-Muff",
      },
    );
    gameOverText.setOrigin(0.5, 0);

    // Score text
    const scoreText = this.add.text(
      width / 2,
      390,
      `${this.keys.strings.catchFoodScene.score}: ${data.score}`,
      {
        fontSize: "40px",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
        fontFamily: "Phantom-Muff",
      },
    );
    scoreText.setOrigin(0.5, 0);

    // Position for additional elements
    let elementsY = 480; // Default position below score

    // Check if this is a new record
    if (data.record !== undefined && data.score === data.record) {
      // New record text
      const recordText = this.add.text(
        width / 2,
        450,
        this.keys.strings.catchFoodScene.newRecord,
        {
          fontSize: "40px",
          color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
          fontFamily: "Phantom-Muff",
          fontStyle: "bold",
        },
      );
      recordText.setOrigin(0.5, 0);
      elementsY = 560; // Position below record text
    }

    // Add energy indicator and text
    const energyIndicator = this.add.image(
      width / 2 - 120,
      elementsY,
      this.atlas,
      this.keys.assets.sprites.ui.indicatorEnergy,
    );
    // energyIndicator.setScale(1);

    const energyText = this.add.text(
      width / 2 - 20,
      elementsY,
      "-10%",
      {
        fontSize: "40px",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
        fontFamily: "Phantom-Muff",
      },
    );
    energyText.setOrigin(0.5, 0.5);

    // Add egg UI image and coins gained text if coinsGained is provided
    if (data.coinsGained !== undefined) {
      const eggImage = this.add.image(
        width / 2 + 80,
        elementsY,
        this.atlas,
        this.keys.assets.sprites.ui.crystalUi,
      );
      // eggImage.setScale(0.5);

      const coinsText = this.add.text(
        width / 2 + 140,
        elementsY,
        `+${data.coinsGained}`,
        {
          fontSize: "40px",
          color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
          fontFamily: "Phantom-Muff",
        },
      );
      coinsText.setOrigin(0.5, 0.5);
    }

    // Restart button
    const restartButton = new RoundedTextButton(
      this, // Вот тут была ошибка, бля передавал this.scene как unknown as BasicOverlay,
      width / 2,
      elementsY + 100,
      this.keys.strings.catchFoodScene.again,
      323,
      72,
      20,
      this.global.TUNER_TWA_CONFIG.palette.success,
      this.global.TUNER_TWA_CONFIG.palette.success + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    );

    restartButton.on("pointerdown", () => {
      data.onRestart();
    });
    this.add.existing(restartButton);

    // Leaderboard button
    const leaderboardButton = new RoundedTextButton(
      this,
      width / 2,
      elementsY + 204,
      this.keys.strings.catchFoodScene.leaderboard,
      323,
      72,
      20,
      this.global.TUNER_TWA_CONFIG.palette.primary,
      this.global.TUNER_TWA_CONFIG.palette.primary + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    );

    leaderboardButton.on("pointerdown", async () => {
      if (this.settings.getSetting("isVibrationEnabled")) {
        this.telegram.twaSDK.hapticFeedback.impactOccurred("medium");
      }

      try {
        // Show loading overlay while making API call
        this.scene.pause();
        this.scene.launch("LoadingOverlay", {
          fromScene: this.scene.key,
          showCloseButton: false,
        });
        this.scene.bringToTop("LoadingOverlay");

        // Call the mini-game leaderboard endpoint
        const response = await this.api.miniGame.getLeaderboard();

        // Close loading overlay
        this.scene.stop("LoadingOverlay");
        this.scene.resume(this.scene.key);

        // Hide the application
        this.telegram.TWA.close();
      } catch (error) {
        console.error("Error calling leaderboard API:", error);

        // Close loading overlay if it's still open
        this.scene.stop("LoadingOverlay");
        this.scene.resume(this.scene.key);

        // Show error message
        const errorMessage = this.keys.strings.catchFoodScene.leaderboardError;
        if (this.global.TWA) {
          this.global.TWA.showAlert(errorMessage);
        } else {
          alert(errorMessage);
        }

        // Still hide the application even if there's an error
        this.telegram.TWA.close();
      }
    });

    this.add.existing(leaderboardButton);

    // Share button (non-functional)
    const shareButton = new RoundedTextButton(
      this,
      width / 2,
      elementsY + 308,
      this.keys.strings.catchFoodScene.share,
      323,
      72,
      20,
      this.global.TUNER_TWA_CONFIG.palette.primary,
      this.global.TUNER_TWA_CONFIG.palette.primary + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    );

    shareButton.on("pointerdown", async () => {
      try {
        this.scene.pause();
        this.scene.launch("LoadingOverlay", {
          fromScene: this.scene.key,
          showCloseButton: false,
        });
        this.scene.bringToTop("LoadingOverlay");

        const resp = await this.api.miniGame.createShareMessage(data.score);

        if (resp.result?.id) {
          sendPreparedMessage(resp.result.id);
        } else {
          console.error("No message ID received from API", resp);
        }
      } catch (error) {
        console.error("Error creating share message:", error);
      } finally {
        this.scene.stop("LoadingOverlay");
        this.scene.resume(this.scene.key);
      }
    });

    this.add.existing(shareButton);

    // Back button
    const backButton = new RoundedTextButton(
      this,
      width / 2,
      elementsY + 409,
      this.keys.strings.catchFoodScene.exit,
      323,
      72,
      20,
      this.global.TUNER_TWA_CONFIG.palette.danger,
      this.global.TUNER_TWA_CONFIG.palette.danger + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    );

    backButton.on("pointerdown", () => {
      if (this.settings.getSetting("isVibrationEnabled")) {
        this.telegram.twaSDK.hapticFeedback.impactOccurred("medium");
      }
      data.onBack();
    });

    this.add.existing(backButton);
  }
}
